type EnvSource = Record<string, string | undefined>;

function readEnv(key: string): string | undefined {
  const viteEnv =
    typeof import.meta !== 'undefined'
      ? (import.meta as ImportMeta & { env?: EnvSource }).env
      : undefined;
  if (viteEnv?.[key]) return viteEnv[key];

  if (typeof process !== 'undefined' && process.env?.[key]) {
    return process.env[key];
  }

  return undefined;
}

function normalizeUrl(value: string | undefined): string | undefined {
  if (!value?.trim()) return undefined;
  const trimmed = value.trim();
  try {
    return new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`).href;
  } catch {
    return undefined;
  }
}

const PRODUCTION_ENTITY_DEFAULTS = {
  linkedin: 'https://www.linkedin.com/company/figtures',
  instagram: 'https://www.instagram.com/figtures.co/',
  clutch: 'https://clutch.co/profile/figtures',
} as const;

export const FIGTURES_LINKEDIN_COMPANY_URL = PRODUCTION_ENTITY_DEFAULTS.linkedin;
export const FIGTURES_INSTAGRAM_URL = PRODUCTION_ENTITY_DEFAULTS.instagram;
export const FIGTURES_CLUTCH_URL = PRODUCTION_ENTITY_DEFAULTS.clutch;

/** External entity profiles — env overrides defaults in production. */
export function getEntityProfileUrls() {
  return {
    linkedin: normalizeUrl(readEnv('VITE_LINKEDIN_URL')) ?? PRODUCTION_ENTITY_DEFAULTS.linkedin,
    instagram: normalizeUrl(readEnv('VITE_INSTAGRAM_URL')) ?? PRODUCTION_ENTITY_DEFAULTS.instagram,
    x: normalizeUrl(readEnv('VITE_X_URL')),
    youtube: normalizeUrl(readEnv('VITE_YOUTUBE_URL')),
    github: normalizeUrl(readEnv('VITE_GITHUB_URL')),
    crunchbase: normalizeUrl(readEnv('VITE_CRUNCHBASE_URL')),
    wikidata: normalizeUrl(readEnv('VITE_WIKIDATA_URL')),
    productHunt: normalizeUrl(readEnv('VITE_PRODUCTHUNT_URL')),
  };
}

/** Schema.org sameAs — external profiles only (no internal page URLs). LinkedIn first for entity disambiguation. */
export function getEntitySameAsUrls(): string[] {
  const { linkedin, instagram, ...rest } = getExtendedEntityProfileUrls();
  const ordered = [linkedin, instagram, ...Object.values(rest)].filter(Boolean) as string[];
  return [...new Set(ordered)];
}

const ENTITY_LINK_LABELS: Record<EntityProfileKey, { tr: string; en: string }> = {
  linkedin: { tr: 'LinkedIn', en: 'LinkedIn' },
  instagram: { tr: 'Instagram', en: 'Instagram' },
  x: { tr: 'X', en: 'X' },
  youtube: { tr: 'YouTube', en: 'YouTube' },
  googleBusiness: { tr: 'Google İşletme', en: 'Google Business' },
  clutch: { tr: 'Clutch', en: 'Clutch' },
  github: { tr: 'GitHub', en: 'GitHub' },
  crunchbase: { tr: 'Crunchbase', en: 'Crunchbase' },
  wikidata: { tr: 'Wikidata', en: 'Wikidata' },
  productHunt: { tr: 'Product Hunt', en: 'Product Hunt' },
};

/** Visible outbound entity links for footer, contact, and crawler HTML signals. */
export function getVisibleEntityLinks(lang: 'tr' | 'en') {
  const profiles = getExtendedEntityProfileUrls();
  return (Object.keys(ENTITY_LINK_LABELS) as EntityProfileKey[])
    .map((key) => {
      const url = profiles[key];
      if (!url) return null;
      return {
        key,
        url,
        label: ENTITY_LINK_LABELS[key][lang],
      };
    })
    .filter((item): item is { key: EntityProfileKey; url: string; label: string } => Boolean(item));
}

export type EntityProfileKey =
  | 'linkedin'
  | 'instagram'
  | 'x'
  | 'youtube'
  | 'github'
  | 'crunchbase'
  | 'wikidata'
  | 'productHunt'
  | 'googleBusiness'
  | 'clutch';

/** Extended profiles used by entity bootstrap validation (not all are sameAs). */
export function getExtendedEntityProfileUrls() {
  return {
    ...getEntityProfileUrls(),
    googleBusiness: normalizeUrl(readEnv('VITE_GOOGLE_BUSINESS_PROFILE_URL')),
    clutch: normalizeUrl(readEnv('VITE_CLUTCH_URL')) ?? PRODUCTION_ENTITY_DEFAULTS.clutch,
  };
}

export function getEntityBootstrapChecklist(): Array<{
  key: EntityProfileKey;
  envVar: string;
  label: string;
  required: boolean;
  sameAs: boolean;
}> {
  return [
    { key: 'linkedin', envVar: 'VITE_LINKEDIN_URL', label: 'LinkedIn Company Page', required: true, sameAs: true },
    { key: 'instagram', envVar: 'VITE_INSTAGRAM_URL', label: 'Instagram', required: false, sameAs: true },
    { key: 'x', envVar: 'VITE_X_URL', label: 'X (Twitter) profile', required: false, sameAs: true },
    { key: 'youtube', envVar: 'VITE_YOUTUBE_URL', label: 'YouTube channel', required: false, sameAs: true },
    { key: 'googleBusiness', envVar: 'VITE_GOOGLE_BUSINESS_PROFILE_URL', label: 'Google Business Profile', required: true, sameAs: true },
    { key: 'clutch', envVar: 'VITE_CLUTCH_URL', label: 'Clutch profile', required: false, sameAs: true },
    { key: 'crunchbase', envVar: 'VITE_CRUNCHBASE_URL', label: 'Crunchbase', required: false, sameAs: true },
    { key: 'wikidata', envVar: 'VITE_WIKIDATA_URL', label: 'Wikidata entity', required: false, sameAs: true },
    { key: 'github', envVar: 'VITE_GITHUB_URL', label: 'GitHub organization', required: false, sameAs: true },
    { key: 'productHunt', envVar: 'VITE_PRODUCTHUNT_URL', label: 'Product Hunt', required: false, sameAs: true },
  ];
}

export function getEntityProfileLinesForDocs(): string[] {
  const profiles = getEntityProfileUrls();
  return Object.entries(profiles)
    .filter(([, url]) => Boolean(url))
    .map(([key, url]) => `- ${key}: ${url}`);
}
